import React, { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { generateHeatmapColorFromVal } from 'common/utils/helpers';
import InputRange from 'components/shared/InputRange';
import './RasterHeatmapSlider.css';

function RasterHeatmapSlider({
  min,
  max,
  value: initialValue,
  heatmap,
  forViewAndExportHeatmap = false,
  hideRange = false,
  onChange
}) {
  const canvasRef = React.useRef(null);
  const [value, setValue] = useState(initialValue);
  const canvasWidth = 100;
  const canvasHeight = forViewAndExportHeatmap ? 5 : 10;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    for (let i = 0; i < canvasWidth; i++) {
      let color = generateHeatmapColorFromVal(i, canvasWidth, 0);
      ctx.fillStyle = 'rgb(' + color.red + ', ' + color.green + ', ' + color.blue + ')';
      ctx.fillRect(i, 0, 1, canvasHeight);
    }

    setValue((prevValue) => {
      if ([min, max].includes(NaN)) return prevValue;
      if (min === -15 || min === -30) return prevValue;

      // As per the requirement, picking the maximum and minimum value from heatmap values.
      const combined = heatmap?.values.flat().map((num) => Math.round(num));
      min = Math.min(...combined) - 5;
      max = Math.max(...combined) + 5;

      const updatedValue = {
        min: min !== prevValue.min && !isNaN(min) ? min : initialValue.min,
        max: max !== prevValue.max && !isNaN(max) ? max : 0
      };
      return updatedValue;
    });
  }, [canvasHeight, min, max]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    for (let i = 0; i < canvasWidth; i++) {
      let color = generateHeatmapColorFromVal(i, canvasWidth, 0);
      ctx.fillStyle = 'rgb(' + color.red + ', ' + color.green + ', ' + color.blue + ')';
      ctx.fillRect(i, 0, 1, canvasHeight);
    }

    // We're not modifying the initial `min` and `max` values hardcoded in `helper.js`, as they might require changes in the future.
    // Instead, we are updating the values here to dynamically calculate the range, ensuring we get the correct `min` value from the heatmap json `.
    setValue((prevValue) => {
      // Only update min if it's different from the previous value
      const newMin = min !== prevValue.min ? min : prevValue.min;

      // Dynamically set max getting from updatedValue from above setValue function, unless min is -15 or -30
      const newMax = min === -15 || min === -30 ? prevValue.max : max;

      // Only return the updated state if min or max has changed
      if (newMin === prevValue.min && newMax === prevValue.max) {
        return prevValue;
      }

      return { min: newMin ? newMin : prevValue.min, max: newMax ? newMax : prevValue.max };
    });
  }, [canvasHeight, min, max]);

  useDebounce(
    () => {
      if (onChange) {
        onChange(value);
      }
    },
    300,
    [value]
  );

  return (
    <div className="RasterHeatmap_Slider">
      <div className="RasterHeatmap_Slider_Canvas_Div">
        <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} />
      </div>
      <div className="RasterHeatmap_Slider_Label_Div">
        <div className="RasterHeatmap_Slider_Label_Inner_Div">{value.min + ' dBm'}</div>
        <div className="RasterHeatmap_Slider_Label_Inner_Div_Middle">
          {Math.round(((value.min + value.max) / 2) * 10) / 10 + ' dBm'}
        </div>
        <div className="RasterHeatmap_Slider_Label_Inner_Div_Right">{value.max + ' dBm'}</div>
      </div>
      {!hideRange && (
        <InputRange
          minValue={min}
          maxValue={max}
          value={{
            min: Math.max(min, Math.min(value.min, max)),
            max: Math.max(min, Math.min(value.max, max))
          }}
          onChange={(v) => setValue(v)}
        />
      )}
    </div>
  );
}

export default RasterHeatmapSlider;
